<script lang="ts">
  import {
    defaultIconCustomisations,
    defaultIconDimensions,
    type IconifyIconCustomisations,
    iconToHTML,
    iconToSVG,
    mergeCustomisations,
    svgToURL,
  } from '@iconify/utils'
  import type { SvelteHTMLElements } from 'svelte/elements'

  import { getIconData } from '$lib/icons'

  type Props = {
    icon: string
    title?: string
  } & IconifyIconCustomisations &
    SvelteHTMLElements['svg']

  let { icon, mode = 'svg', title, ...restProps }: Props = $props()

  /**
   * Default SVG attributes
   */
  const svgDefaults = {
    xmlns: 'http://www.w3.org/2000/svg',
    'xmlns:xlink': 'http://www.w3.org/1999/xlink',
    'aria-hidden': true,
    role: 'img',
  }

  /**
   * Style modes
   */
  const commonProps: Record<string, string> = {
    display: 'inline-block',
  }

  const monotoneProps: Record<string, string> = {
    'background-color': 'currentColor',
  }

  const coloredProps: Record<string, string> = {
    'background-color': 'transparent',
  }

  // Dynamically add common props to variables above
  const propsToAdd: Record<string, string> = {
    image: 'var(--svg)',
    repeat: 'no-repeat',
    size: '100% 100%',
  }
  const propsToAddTo: Record<string, Record<string, string>> = {
    '-webkit-mask': monotoneProps,
    mask: monotoneProps,
    background: coloredProps,
  }
  for (const prefix in propsToAddTo) {
    const list = propsToAddTo[prefix]
    for (const prop in propsToAdd) {
      list[prefix + '-' + prop] = propsToAdd[prop]
    }
  }

  /**
   * Fix size: add 'px' to numbers
   */
  function fixSize(value: string): string {
    return value + (value.match(/^[-0-9.]+$/) ? 'px' : '')
  }

  const iconInfo = $derived.by(() => {
    const iconData = getIconData(icon)
    if (!iconData) {
      return null
    }

    const defaultExtendedIconCustomisations = {
      ...defaultIconCustomisations,
      inline: false,
    }

    // Use iconToSVG to generate SVG attributes and body
    const customisations = mergeCustomisations(defaultExtendedIconCustomisations, restProps)

    const svgIcon = iconToSVG(iconData, customisations)

    const componentProps = {
      ...(mode === 'svg' ? { ...svgDefaults, title } : {}),
    } as Record<string, unknown>

    if (mode === 'svg') {
      return {
        svg: true,
        attributes: { ...componentProps, ...svgIcon?.attributes },
        body: svgIcon.body,
      }
    }

    const { body, width, height } = iconData

    const useMask = mode === 'mask' || (mode === 'bg' ? false : body.indexOf('currentColor') !== -1)

    const htmlIcon = iconToHTML(body, {
      ...svgIcon?.attributes,
      width: width + '',
      height: height + '',
    })

    const url = svgToURL(htmlIcon)
    const styles: Record<string, string | undefined> = {
      '--svg': url,
      width: restProps.width
        ? typeof restProps.width === 'number'
          ? fixSize(restProps.width.toString())
          : restProps.width
        : fixSize(defaultIconDimensions.width.toString()),
      height: restProps.height
        ? typeof restProps.height === 'number'
          ? fixSize(restProps.height.toString())
          : restProps.height
        : fixSize(defaultIconDimensions.height.toString()),
      ...commonProps,
      ...(useMask ? monotoneProps : coloredProps),
    }

    let customStyle = ''
    for (const key in styles) {
      customStyle += key + ': ' + styles[key] + ';'
    }

    componentProps.style = customStyle + restProps.style

    return {
      svg: false,
      attributes: componentProps,
    }
  })
</script>

{#if iconInfo}
  {#if iconInfo.svg}
    <svg {...iconInfo.attributes}>
      {#if title}
        <title>{title}</title>
      {/if}
      <!-- eslint-disable-next-line svelte/no-at-html-tags This is a valid use case for SVGs from Iconify -->
      {@html iconInfo.body}
    </svg>
  {:else}
    <span {...iconInfo.attributes} {title}></span>
  {/if}
{:else}
  <!-- Fallback for missing icons -->
  <span
    {...restProps as SvelteHTMLElements['span']}
    class="missing-icon {restProps.class}"
    title="Missing icon: {icon}"
  >
    ⚠️
  </span>
{/if}

<style>
  .missing-icon {
    display: inline-block;
    font-size: 1em;
    opacity: 0.5;
  }
</style>
